/* eslint-disable @typescript-eslint/no-unused-vars */
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import getDay from 'date-fns/getDay';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';
import { ja } from 'date-fns/locale';
import * as _ from 'lodash';
import {
  PAYMENT_STATUS,
  PLAN_PAYMENT_METHOD,
  PLAN_PAYMENT_METHOD_TEXT,
  REPEAT_TYPE,
  SCORE_REACHES_RULE,
  SCORE_TYPE,
  SELECTION_STATUS,
  TOURNAMENT_GAME_GAME_STATUS,
  TRANSFER_STATUS_TEXT,
} from '@/constants';
import { Member } from '@/types/Member';
import { ReservationInfo } from '@/types/Reservation';
import { School } from '@/types/School';
import { IGameResult, IGameRules, IPlayer, IScore } from '@/types/tournament/ScoreTournament';
export const days = ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'];

export const daysShort = ['日', '月', '火', '水', '木', '金', '土'];

export const daysEnglish = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export const months = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export const dayMap = daysEnglish.reduce(
  (
    acc: {
      [key: string]: string;
    },
    dayEnglish: string,
    i: number,
  ): { [key: string]: string } => {
    return {
      ...acc,
      [dayEnglish]: days[i],
    };
  },
  {},
);

// yyyy-MM-ddを返す
export const formatISODate = (date: Date = new Date()): string => {
  return formatISO(date, { representation: 'date' });
};

// yyyy/MM/ddを返す
export const formatDisplayDate = (date: Date = new Date()): string => {
  return format(date, 'yyyy/MM/dd');
};

// yyyy/MMを返す
export const formatDisplayYearMonth = (date: Date = new Date()): string => {
  return format(date, 'yyyy/MM');
};

// yyyy-MMを返す
export const formatDisplayYearMonthPattern = (date: Date = new Date(), pattern = 'yyyy-MM'): string => {
  return format(date, pattern);
};

// mm:ddを返す
export const formatTime = (date: Date = new Date()): string => {
  const hour = String(getHours(date)).padStart(2, '0');
  const minute = String(getMinutes(date)).padStart(2, '0');

  return `${hour}:${minute}`;
};

// mm:ddを返す
export const formatTimeByLessonTime = (objectTime?: { hour: number | string; minute: number }): string => {
  return objectTime ? `${objectTime.hour}:${String(objectTime.minute).padStart(2, '0')}` : '';
};

// mm:dd - mm:ddを返す
export const formatTimeRange = (time?: {
  start: {
    hour: number;
    minute: number;
  };
  end: {
    hour: number;
    minute: number;
  };
}): string => {
  if (time) {
    const { start, end } = time;
    const startTime = `${start.hour}:${String(start.minute).padStart(2, '0')}`;
    const endTime = `${end.hour}:${String(end.minute).padStart(2, '0')}`;

    return `${startTime} - ${endTime}`;
  } else {
    return '';
  }
};

// mm:dd - mm:ddを返す
export const formatBusinessHour = (time: {
  open: {
    hour: number | string;
    minute: number | string;
  };
  close: {
    hour: number | string;
    minute: number | string;
  };
}) => {
  if (time) {
    const { open, close } = time;
    const openTime = `${open.hour}:${String(open.minute).padStart(2, '0')}`;
    const closeTime = `${close.hour}:${String(close.minute).padStart(2, '0')}`;

    return { openTime, closeTime };
  } else {
    return { openTime: '', closeTime: '' };
  }
};

// mm:dd - mm:ddを返す
export const formatBusinessHourMMDD = (time: {
  open: {
    hour: number | string;
    minute: number | string;
  };
  close: {
    hour: number | string;
    minute: number | string;
  };
}) => {
  if (time) {
    const { open, close } = time;
    const openTime = `${String(open.hour).padStart(2, '0')}:${String(open.minute).padStart(2, '0')}`;
    const closeTime = `${String(close.hour).padStart(2, '0')}:${String(close.minute).padStart(2, '0')}`;

    return { openTime, closeTime };
  } else {
    return { openTime: '', closeTime: '' };
  }
};

/**
 * to convert data
 * @param startTime
 * @param endTime
 * @returns object
 */
export const formatLessonTime = (startTime?: string, endTime?: string) => {
  if (!startTime || !endTime) {
    return null;
  }
  const startTimeSplit = startTime.split(':');
  const endTimeSplit = endTime.split(':');
  return {
    start: {
      hour: startTimeSplit[0],
      minute: Number(startTimeSplit[1]),
    },
    end: {
      hour: endTimeSplit[0],
      minute: Number(endTimeSplit[1]),
    },
  };
};

/**
 * to convert data
 * @param startTime
 * @param endTime
 * @returns object
 */
export const formatOpenCloseTime = (startTime?: string, endTime?: string) => {
  if (!startTime || !endTime) {
    return null;
  }
  const startTimeSplit = startTime.split(':');
  const endTimeSplit = endTime.split(':');
  return {
    open: {
      hour: startTimeSplit[0],
      minute: startTimeSplit[1],
    },
    close: {
      hour: endTimeSplit[0],
      minute: endTimeSplit[1],
    },
  };
};

// mm:dd - mm:ddを返す
export const formatTimeRangeMMDD = (startTime: string, endTime: string): string => {
  return `${startTime} - ${endTime}`;
};

// mm:ddを返す
export const formatTimeMMDD = (hour: number | string, minute: number): string => {
  if (hour) {
    return `${hour}:${String(minute).padStart(2, '0')}`;
  } else {
    return '';
  }
};

// 日付から曜日を取得
export const getDayByDate = (date: Date = new Date(), locale: 'ja' | 'ja_short' | 'en' = 'ja'): string => {
  const index = getDay(date);
  const daysArray = locale === 'ja' ? days : locale === 'ja_short' ? daysShort : daysEnglish;
  return daysArray[index];
};

// 引数の日にちが『月の第n週か』を返す
export const getNumberWeek = (date: Date = new Date()) => {
  return Math.floor((date.getDate() - 1) / 7) + 1;
};

// レッスン / 定休日の繰り返し回数と間隔を返す
export const getRepeatedClosedDay = (
  repeat: number,
): {
  count: number;
  interval: number;
} => {
  return (() => {
    switch (repeat) {
      case REPEAT_TYPE.WEEKLY:
        return {
          count: 8,
          interval: 7,
        };
      case REPEAT_TYPE.MONTHLY:
        return {
          count: 2,
          interval: 1,
        };
      case REPEAT_TYPE.YEARLY:
        return {
          count: 2,
          interval: 1,
        };
      default:
        return {
          count: 1,
          interval: 0,
        };
    }
  })();
};

// 値を上限と下限の間に制限
// (月表示カレンダーの高さ制限で使用)
export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

export const getMaxObjectArray = (array?: any[]): number => {
  return array && array?.length > 0 ? Math.max(...array.map((o) => o.number)) || 0 : 0;
};

/**
 * @param: hour
 * @param: minutes
 * return minutes
 */
export const calMinutes = (hour: number, minutes: number) => {
  return hour * 60 + minutes;
};

// yyyy年MM月dd日を返す
export const formatDateJP = (date: Date = new Date()): string => {
  return format(date, 'yyyy年MM月dd日', { locale: ja });
};

// yyyy年MM月を返す
export const formatDateJPYYYYMM = (date: Date = new Date()): string => {
  return format(date, 'yyyy年MM月', { locale: ja });
};
/**
 *
 * @param date
 * @param months
 * @returns string
 */
export const dateWithMonthsDelay = (date = new Date(), months: number) => {
  date.setMonth(date.getMonth() + months);
  return format(date, 'yyyy/MM/dd');
};

// convert currency strings to xx,xxx.xx format
export const formatPrice = (price?: string, textLast?: string): string => {
  if (price) {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + textLast;
  }
  return '';
};

export const formatDisplayYYYYMMDDDate = (date: Date = new Date()): string => {
  return format(date, 'yyyy-MM-dd');
};

// yyyy/MM/ddを返す
export const formatDisplayDateAndHour = (date: Date = new Date()): string => {
  return format(date, 'yyyy/MM/dd HH:mm');
};

export const getDateInfor = (iso: string) => {
  const date = new Date(iso);
  return {
    month: date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1,
    year: date.getFullYear(),
    date: date.getDate() < 10 ? '0' + date.getDate() : date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
  };
};

export const getMailBody = (member: Member, reservationInfo: ReservationInfo, schoolInfo?: School | null) => {
  let startTime = '';
  let endTime = '';
  if (schoolInfo?.school_business_hour) {
    const { open, close } = schoolInfo?.school_business_hour;
    startTime = `${open.hour}:${String(open.minute).padStart(2, '0')}`;
    endTime = `${close.hour}:${String(close.minute).padStart(2, '0')}`;
  }

  const params = [
    member.last_name + ' ' + member.first_name,
    schoolInfo?.school_name,
    reservationInfo.schedule.lesson?.lesson_name,
    reservationInfo?.reserve_event_dates ? reservationInfo?.reserve_event_dates[0].event_date.event_date : '',
    reservationInfo.schedule.start_time,
    reservationInfo.schedule.end_time,
    schoolInfo?.school_name,
    schoolInfo?.school_tel,
    schoolInfo?.school_mail,
    schoolInfo?.school_address,
    startTime,
    endTime,
  ];
  return {
    to: [member.member_account?.email],
    from: process.env.NEXT_PUBLIC_SYSTEM_EMAIL_ADDRESS,
    param: params,
  };
};

export const getResetPasswordVerificationMailLink = (email: string, link: string) => {
  const MAIL_TEMPLATE = `
  お客様

  いつもスポハビをご利用いただき、誠にありがとうございます。
  パスワードリセットの申請を承りました。

  下記のURLをクリックし、新しいパスワードをご登録ください。
  パスワードの再設定URL: ${link}

  今後ともスポハビをよろしくお願いいたします。

  このメールは自動配信のため、ご返信いただいてもお答えできません。
  お問い合わせは下記までご連絡ください。
  ------------------------------------------------------------
  お問い合わせフォーム: https://official.spohabi.com/contact/
  ------------------------------------------------------------
  本メールにお心当たりのない方は、お手数ですが本メールを破棄してください。
  `;

  return {
    to: [email],
    from: 'system.tennis@spohabi.com',
    subject: '【スポハビ】パスワードリセットのご確認',
    text: MAIL_TEMPLATE,
  };
};

export const getVerifyAndChangeEmailLink = (newEmail: string, link: string) => {
  const MAIL_TEMPLATE = `
  お客様

  いつもスポハビをご利用いただき、誠にありがとうございます。
  メールアドレスの変更を承りました。

  下記のリンクをクリックし、変更を完了してください。
  ${link}

  このメールは自動配信のため、ご返信いただいてもお答えできません。
  お問い合わせは下記までご連絡ください。
  ------------------------------------------------------------
  お問い合わせフォーム: https://official.spohabi.com/contact/
  ------------------------------------------------------------
  本メールにお心当たりのない方は、お手数ですが本メールを破棄してください。
  `;

  return {
    to: [newEmail],
    from: 'system.tennis@spohabi.com',
    subject: '【スポハビ】メールアドレス変更のご確認',
    text: MAIL_TEMPLATE,
  };
};

/**
 * Display payment method as text
 * @param planPaymentMethod
 * @returns
 */
export const planPaymentMethodText = (planPaymentMethod: number | null) => {
  switch (planPaymentMethod) {
    case null:
      return '未設定';
    case PLAN_PAYMENT_METHOD.BANK_ACCOUNT_TRANSFER:
    case PLAN_PAYMENT_METHOD.CREDIT_CARD:
    case PLAN_PAYMENT_METHOD.GROUP_TOTAL:
    case PLAN_PAYMENT_METHOD.ZENGIN_DATA:
    case PLAN_PAYMENT_METHOD.CASH:
      return PLAN_PAYMENT_METHOD_TEXT[planPaymentMethod];
    default:
      return '支払い方法なし';
  }
};

export const getReservationMethodText = (reservationMethod: number): string => {
  switch (reservationMethod) {
    case 1:
      return 'チケットで予約';
    case 2:
      return '在籍クラス';
    case 3:
      return '代理予約（窓口で精算）';
    case 4:
      return '代理予約（チケット消費）';
    case 5:
      return 'イベント予約（窓口で精算）';
    case 6:
      return 'イベント予約（カード決済）';
    default:
      return 'チケットで予約';
  }
};

/**
 * Generate array of number
 * @param start
 * @param stop
 * @param step
 * @returns Array of number
 */
export const rangeArr = (start: any, stop: any, step: any) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

export const getAttendanceText = (key: string | number) => {
  switch (key) {
    case '1':
    case 1:
      return '出席予定';
    case '2':
    case 2:
      return '出席';
    case '3':
    case 3:
      return '無断欠席';
    case '4':
    case 4:
      return '欠席';
  }
};

/*Hàm tính khoảng cách giữa 2 ngày trong javascript*/
export const getDayOffTime = (d1: Date, d2: Date) => {
  const ms1 = d1.getTime();
  const ms2 = d2.getTime();
  return Math.ceil((ms2 - ms1) / (24 * 60 * 60 * 1000));
};

export const getApplyText = (key: string | number) => {
  switch (key) {
    case SELECTION_STATUS.PARTICIPANT:
      return '参加確定';
    case SELECTION_STATUS.CANCEL:
      return 'キャンセル';
    case SELECTION_STATUS.NOT_PARTICIPANT:
      return '落選';
    case SELECTION_STATUS.TEMPORARY_REGISTRATION:
      return '仮申込';
    case SELECTION_STATUS.STATUS_ALT:
      return 'キャンセル待ち';
    case SELECTION_STATUS.SELECTED_NOT_PAID:
      return '当選';
    default:
      return '選考中';
  }
};

/**
 * Calculate factorial sum of number
 * @param n
 * @returns
 */
export const factorialSum = (num: number): number => {
  let result = 1;
  for (let i = 2; i <= num; i++) result = result + i;
  return result;
};

/**
 * Old matrix
 * @param dimension
 * @returns
 */
export const _symmetricMatrix = (dimension: number) => {
  const matrix = Array.from(Array(dimension), () => new Array(dimension));
  let value = 1;
  for (let i = 0; i < dimension; i++) {
    for (let j = 0; j <= i; j++) {
      if (i === j) {
        matrix[i][j] = 0;
        matrix[j][i] = 0;
      } else {
        matrix[i][j] = value;
        matrix[j][i] = value;
        value = value + 1;
      }
    }
  }
  return matrix;
};

/**
 * Generate compressed forms of a symmetric matrix
 * @param dimension
 * @return {Number[][]}
 */
export const symmetricMatrix = (dimension: number) => {
  const matrix = Array.from(Array(dimension), () => new Array(dimension));

  const matchPairs = robinRound(dimension);

  for (let i = 0; i < dimension; i++) {
    matrix[i][i] = 0;
  }

  for (let index = 0; index < matchPairs.length; index++) {
    const matchPair = matchPairs[index];
    matrix[matchPair[0]][matchPair[1]] = index + 1;
    matrix[matchPair[1]][matchPair[0]] = index + 1;
  }

  return matrix;
};

/**
 * Generate league robin round
 * @param numberOfPlayers
 * @returns
 */
const robinRound = (numberOfPlayers: number) => {
  const matchParticipants = (players: number[]) => {
    const _players = Array.from(players);
    const matchPairs = [];
    while (_players.length != 0) {
      const player1 = _players.shift();
      const player2 = _players.pop();
      if (player2 != undefined && player2 !== -1 && player1 != undefined && player1 !== -1) {
        matchPairs.push([player1, player2]);
      }
    }
    return matchPairs;
  };

  const rotateArray = (array: number[]): any[] => {
    const p = Array.from(array);
    const firstElement = p.shift();
    const lastElement = p.pop();
    return [firstElement, lastElement, ...p];
  };

  const generateTournament = (players: number[]) => {
    const tournamentRounds = [];
    const rounds = players.length - 1;
    let _players: number[] = Array.from(players);
    for (let i = 0; i < rounds; i++) {
      tournamentRounds.push(...matchParticipants(_players));
      _players = rotateArray(_players);
    }
    return tournamentRounds;
  };

  const playersNo = Array.from(Array(numberOfPlayers).keys());
  if (numberOfPlayers % 2 == 1) {
    playersNo.push(-1);
  }

  return generateTournament(playersNo);
};

export const getMultiDayOfWeek = (dayOfTheWeek?: number[] | null): string => {
  let daysText = '';
  if (dayOfTheWeek && dayOfTheWeek.length > 0) {
    daysText = dayOfTheWeek.map((dayNumber) => days[dayNumber]).join('、');
  }
  return daysText;
};

export const isCheckExist2902 = (sEndDate: Date, yearNow: number): boolean => {
  const isFebruaryEndDate = sEndDate.getMonth() == 1 && sEndDate.getDate() == 28;
  const date292 = new Date(`${yearNow}/02/29`);
  return isFebruaryEndDate && date292.getDate() === 29;
};

// calc score for tournament

export const setZero = (player: IPlayer) => {
  player.score = [
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
  ];
  player.completedGameCount = 0;
  player.wonSetCount = 0;
  player.wonGameCount = 0;
};

export const setAllWin = (player: IPlayer, gameRules: IGameRules, isDEF?: boolean, playerInit?: IScore[]) => {
  player.score = [
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
  ];
  const winningConditionSetCount =
    gameRules.scoreType === SCORE_TYPE.MULTI_SET ? Math.floor((gameRules.bestOfSetsFormat + 1) / 2) : 1;
  if (isDEF && gameRules.isNoLimitGameInSets && playerInit) {
    for (let i = 0; i < playerInit.length; i++) {
      if (!!playerInit[i].game) {
        player.score[i].game = playerInit[i].game;
        player.score[i].won = true;
      }
    }
  } else {
    for (let i = 0; i < winningConditionSetCount; i++) {
      player.score[i].game = gameRules.maxGamesInSets;
      player.score[i].won = true;
    }
  }

  player.completedGameCount = 1;
  player.wonSetCount = winningConditionSetCount;
  player.wonGameCount = 0;
};

export const setWinRemaining = (player1: IPlayer, player2: IPlayer, gameResult: IGameResult) => {
  let wonPlayerScore: IScore[] = [];
  let losePlayerScore: IScore[] = [];
  const wonPlayerScoreForResult: IScore[] = [
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
  ];
  const losePlayerScoreForResult: IScore[] = [
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
    { game: 0, won: null },
  ];

  switch (gameResult.status.target) {
    case player1.playerId:
      wonPlayerScore = gameResult.score.player2;
      losePlayerScore = gameResult.score.player1;
      player1.score = losePlayerScoreForResult;
      player2.score = wonPlayerScoreForResult;
      player2.completedGameCount = 1;
      break;

    case player2.playerId:
      wonPlayerScore = gameResult.score.player1;
      losePlayerScore = gameResult.score.player2;
      player2.score = losePlayerScoreForResult;
      player1.score = wonPlayerScoreForResult;
      player1.completedGameCount = 1;
      break;

    default:
      break;
  }
  const winningConditionSetCount =
    gameResult.gameRules.scoreType === SCORE_TYPE.MULTI_SET ? gameResult.gameRules.bestOfSetsFormat : 1;
  // Create init score for game
  if (wonPlayerScore.length < winningConditionSetCount) {
    const setCompare = winningConditionSetCount - wonPlayerScore.length;
    for (let index = 0; index < setCompare; index++) {
      wonPlayerScore.push({ game: 0, won: null });
      losePlayerScore.push({ game: 0, won: null });
    }
  }
  let completedGameCount = 1;
  let completedSetCount = 0;

  const winningMaxSetCount = (winningConditionSetCount + 1) / 2;
  for (let i = 0; i < wonPlayerScore.length; i++) {
    const winnerScore = wonPlayerScore[i];
    const loserScore = losePlayerScore[i];

    if (winnerScore.won === null) {
      if (!gameResult.gameRules.isNoLimitGameInSets) {
        if (
          winnerScore.game &&
          winnerScore.game >= gameResult.gameRules.maxGamesInSets - 1 &&
          loserScore.game &&
          loserScore.game >= gameResult.gameRules.maxGamesInSets - 1
        ) {
          let maxGameCount = 0;
          switch (gameResult.gameRules.scoreReaches) {
            case SCORE_REACHES_RULE.NEXT_GAME_ENDS:
              maxGameCount = gameResult.gameRules.maxGamesInSets;
              break;
            case SCORE_REACHES_RULE.TWO_GAME_WIN:
              maxGameCount = gameResult.gameRules.maxGamesInSets + 1;
              break;
            default:
              break;
          }
          wonPlayerScoreForResult[i].game = maxGameCount;
        } else {
          wonPlayerScoreForResult[i].game = gameResult.gameRules.maxGamesInSets;
        }
      } else {
        wonPlayerScoreForResult[i].game = winnerScore.game;
      }
      wonPlayerScoreForResult[i].won = true;
      losePlayerScoreForResult[i].game = loserScore.game;
      losePlayerScoreForResult[i].won = false;
      completedSetCount++;
      if (completedSetCount === winningMaxSetCount) {
        break;
      }
      for (let j = i + 1; j < wonPlayerScore.length; j++) {
        if (!gameResult.gameRules.isNoLimitGameInSets) {
          wonPlayerScoreForResult[j].game = gameResult.gameRules.maxGamesInSets;
        } else {
          wonPlayerScoreForResult[j].game = 0;
        }
        wonPlayerScoreForResult[j].won = true;
        losePlayerScoreForResult[j].game = 0;
        losePlayerScoreForResult[j].won = false;
        completedSetCount++;
        if (completedSetCount === winningMaxSetCount) {
          break;
        }
      }
      break;
    } else {
      if (winnerScore.won === true) {
        completedSetCount++;
      }
      wonPlayerScoreForResult[i] = winnerScore;
      losePlayerScoreForResult[i] = loserScore;
      // check trường hợp trận đấu chỉ có 1 set
      if (gameResult.gameRules.scoreType === SCORE_TYPE.SINGLE_SET) {
        if (!gameResult.gameRules.isNoLimitGameInSets) {
          if (
            winnerScore.game &&
            winnerScore.game >= gameResult.gameRules.maxGamesInSets - 1 &&
            loserScore.game &&
            loserScore.game >= gameResult.gameRules.maxGamesInSets - 1
          ) {
            let maxGameCount = 0;
            switch (gameResult.gameRules.scoreReaches) {
              case SCORE_REACHES_RULE.NEXT_GAME_ENDS:
                maxGameCount = gameResult.gameRules.maxGamesInSets;
                break;
              case SCORE_REACHES_RULE.TWO_GAME_WIN:
                maxGameCount = gameResult.gameRules.maxGamesInSets + 1;
                break;
              default:
                break;
            }
            wonPlayerScoreForResult[i].game = maxGameCount;
          } else {
            wonPlayerScoreForResult[i].game = gameResult.gameRules.maxGamesInSets;
          }
        } else {
          wonPlayerScoreForResult[i].game = winnerScore.game;
        }
      }

      completedGameCount++;
    }
  }
};

export const calcScore = (gameResult: IGameResult, player1Id?: number | null, player2Id?: number | null) => {
  const player1: IPlayer = {
    score: [],
    completedGameCount: 0,
    wonSetCount: 0,
    wonGameCount: 0,
    playerId: player1Id || undefined,
  };

  const player2: IPlayer = {
    score: [],
    completedGameCount: 0,
    wonSetCount: 0,
    wonGameCount: 0,
    playerId: player2Id || undefined,
  };

  switch (gameResult.status.type) {
    // No Show,WO,DEFの場合は、負けた選手の取得ゲーム数がすべて0として計算(負けた選手は試合完了数0)
    case TOURNAMENT_GAME_GAME_STATUS.NO_SHOW:
    case TOURNAMENT_GAME_GAME_STATUS.W_O:
    case TOURNAMENT_GAME_GAME_STATUS.DEF:
      switch (gameResult.status.target) {
        case player1Id:
          setZero(player1);
          setAllWin(
            player2,
            gameResult.gameRules,
            TOURNAMENT_GAME_GAME_STATUS.DEF === gameResult.status.type,
            gameResult.score.player2,
          );
          break;
        case player2Id:
          setZero(player2);
          setAllWin(
            player1,
            gameResult.gameRules,
            TOURNAMENT_GAME_GAME_STATUS.DEF === gameResult.status.type,
            gameResult.score.player1,
          );
          break;
        default:
          break;
      }
      break;

    // RETの場合は、
    // 負けた選手:棄権するまでに取得したゲーム数をそのまま結果に反映、残りゲームはすべて取得ゲーム数0として計算
    // 勝った選手:棄権するまでに取得したゲーム数をそのまま結果に反映、残りゲームはすべて取得したものとして計算
    // 両選手試合完了数1として計算
    case TOURNAMENT_GAME_GAME_STATUS.RET:
      setWinRemaining(player1, player2, gameResult);
      break;

    // Not Playedは両選手取得ゲーム数0、試合完了数0として計算
    case TOURNAMENT_GAME_GAME_STATUS.NOT_PLAYED:
      setZero(player1);
      setZero(player2);
      break;

    default:
      player1.completedGameCount = 1;
      player2.completedGameCount = 1;
      break;
  }

  player1.wonGameCount = player1.score.reduce(function (sum, element) {
    if (element.game) {
      return sum + element.game;
    } else {
      return sum;
    }
  }, 0);
  player1.wonSetCount = player1.score.filter((s) => s.won === true).length;
  player2.wonGameCount = player2.score.reduce(function (sum, element) {
    if (element.game) {
      return sum + element.game;
    } else {
      return sum;
    }
  }, 0);
  player2.wonSetCount = player2.score.filter((s) => s.won === true).length;

  return {
    player1: player1,
    player2: player2,
  };
};

/**
 * Get knockout matches by seed
 * @param params
 * @param {number} params.numberOfPlayers  - プレイヤー 数
 * @param {number} params.maxSeedNumber - シード 数
 * @returns matrix of match
 */
export const getKnockoutMatchesBySeed = ({ numberOfPlayers }: { numberOfPlayers: number }) => {
  const participants = Array.from({ length: numberOfPlayers }, (_, i) => i + 1);

  /**
   * Helper function
   * Get seed number
   * @param seed
   * @param participantsCount
   * @returns {number|null} - seed number
   */
  const getSeed = (seed: number | null, participantsCount: number) => {
    return seed ? (seed <= participantsCount ? seed : null) : null;
  };

  /**
   * Helper function
   * Format to seed group number
   * @param seed
   * @returns {string}
   */
  const formatSeed = (seed: number | null) => {
    if (seed) {
      if (seed === 1 || seed === 2) {
        return `${seed}`;
      } else if (seed >= 3 && seed <= 4) {
        return '3~4';
      } else if (seed >= 5 && seed <= 8) {
        return '5~8';
      } else if (seed >= 9 && seed <= 16) {
        return '9~16';
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  /**
   * Helper function
   * Get matches, highest seeding priority
   * @param {array} matches
   * @param {number} totalMatches - The number of matches to get
   * @returns
   */
  const breakDown = ({ matches, totalMatches }: { matches: (string | null)[][]; totalMatches: number }) => {
    const middleMatch = totalMatches / 2;

    const formatedMatches = [];
    let index = 0;
    do {
      formatedMatches[totalMatches - index - 1] = matches[matches.length - index - 1];
      formatedMatches[index] = matches[index];
      ++index;
    } while (index < middleMatch);

    return formatedMatches;
  };

  /**
   * Main function
   * @returns maxtrix matches
   */
  const generate = () => {
    const totalPlayers = participants.length;

    if (totalPlayers < 2 || totalPlayers % 2 !== 0) {
      return [];
    }

    const estimatedRounds = Math.ceil(Math.log(totalPlayers) / Math.log(2));
    let matches: (number | null)[][] = [[1, 2]];

    for (let round = 1; round < estimatedRounds; round++) {
      const roundMatches = [];
      const estimateSum = Math.pow(2, round + 1) + 1;

      for (let i = 0; i < matches.length; i++) {
        let firstPlayer = getSeed(matches[i][0], Math.pow(2, estimatedRounds));
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        let secondPlayer = getSeed(matches[i][0] ? estimateSum - matches[i][0] : null, Math.pow(2, estimatedRounds));
        roundMatches.push([firstPlayer, secondPlayer]);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        firstPlayer = getSeed(estimateSum - matches[i][1], Math.pow(2, estimatedRounds));
        secondPlayer = getSeed(matches[i][1], Math.pow(2, estimatedRounds));
        roundMatches.push([firstPlayer, secondPlayer]);
      }
      matches = roundMatches;
    }

    let matchesWithSeed = matches.map(([firstPlayer, secondPlayer]) => {
      return [formatSeed(firstPlayer), formatSeed(secondPlayer)];
    });

    /**
     * Reformat matches
       In case the number of players is not a power of 2, for exemple != 2, 4, 8, 16, 32, 64
     */
    if (((Math.log(totalPlayers) / Math.log(2)) * 2) % 2 !== 0) {
      let formatedMatches: (string | null)[][] = [];
      const nrMatchesToBreakDown = Math.pow(2, estimatedRounds) / 16;
      const breakMatches = _.chunk(matchesWithSeed, nrMatchesToBreakDown > 4 ? nrMatchesToBreakDown : 4);

      let setMatches = 0;

      for (let index = 0; index < breakMatches.length; index++) {
        const breakMatch = breakMatches[index];

        formatedMatches = [
          ...formatedMatches,
          ...breakDown({
            matches: breakMatch,
            totalMatches:
              index !== breakMatches.length - 1
                ? Math.floor(totalPlayers / breakMatches.length / 2)
                : Math.floor(totalPlayers / 2 - setMatches),
          }),
        ];

        setMatches = formatedMatches.length;
      }

      matchesWithSeed = formatedMatches;
    }

    return matchesWithSeed;
  };

  return generate();
};

export const getRepeatTypeLabel = (repeatType: number) => {
  switch (repeatType) {
    case REPEAT_TYPE.WEEKLY:
      return '毎週';
    case REPEAT_TYPE.MONTHLY:
      return '毎月';
    case REPEAT_TYPE.YEARLY:
      return '毎年';
    case REPEAT_TYPE.NONE:
      return '繰り返さない';
    default:
      return '';
  }
};

export const getDisplayCloseDayTitle = (date: string, type: number) => {
  switch (type) {
    case REPEAT_TYPE.YEARLY:
    case REPEAT_TYPE.MONTHLY:
      return `${date}から${getRepeatTypeLabel(type)}第${getNumberWeek(new Date(date))}${
        dayMap[getDayByDate(new Date(date), 'en')]
      }を定休日`;
    case REPEAT_TYPE.WEEKLY:
      return `${date}から${getRepeatTypeLabel(type)}${dayMap[getDayByDate(new Date(date), 'en')]}を定休日`;
    case REPEAT_TYPE.NONE:
      return `${date}(${dayMap[getDayByDate(new Date(date), 'en')]})を定休日`;
    default:
      return '';
  }
};

export const splitTime = (time: string) => {
  const _time = time.split(':');

  return {
    hour: Number(_time[0]),
    minute: Number(_time[1]),
  };
};

/**
 * Convert payment status from number to string
 * @param {number} status
 * @returns {string}
 */
export const convertPaymentStatus = (status: number | null): string => {
  return status || status == 0 ? TRANSFER_STATUS_TEXT[status] : '';
};

export const moveElement = (array: any[], from: number, to: number) => {
  if (from >= 0) {
    const element = array.splice(from, 1)[0]; // Lấy và xóa phần tử tại vị trí `from`
    array.splice(to, 0, element); // Chèn phần tử vào vị trí `to`
  }
  return array;
};
/**
 * get Color Transfer Status
 * @param {number} status
 * @returns {bg: string, color: string}
 */
export const getColorTransferStatus = (status: number | null) => {
  switch (status) {
    case PAYMENT_STATUS.INPROGRESS:
      return {
        bg: '#FFF6D3',
        color: '#47464A',
      };
    case PAYMENT_STATUS.FIXED_AMOUNT:
      return {
        bg: '#FFF6D3',
        color: '#303033',
      };
    case PAYMENT_STATUS.CASH_RECEIVED:
    case PAYMENT_STATUS.COMPLETED:
      return {
        bg: '#E6FBF6',
        color: '#2D8F8E',
      };
    case PAYMENT_STATUS.SCHEDULED:
    case PAYMENT_STATUS.FAIL:
    case PAYMENT_STATUS.CANCELLED:
      return {
        bg: '#FFEEEB',
        color: '#EA503B',
      };
    case PAYMENT_STATUS.NOPAY:
      return {
        bg: '#fff',
        color: '#AAAAAA',
      };
    default:
      return {
        bg: '#fff',
        color: '#303033',
      };
  }
};
